/**
 * 북마크(링크 카드 / 임베드) 블록 — 인라인 링크에서 "전환"으로 생성.
 * 블록 JSON: { type:"bookmark",
 *   props:{ url, mode:"card"|"embed", title?, description?, image?, site?, favicon?,
 *           provider?, embedUrl?, ratio? },
 *   content:[], children:[] }
 *  - mode="card": 썸네일 + 제목/설명/도메인 카드(<a target=_blank>). 메타는 서버(fetchLinkPreview)로 채움.
 *  - mode="embed": YouTube/Vimeo 만 iframe(제공자 화이트리스트). 그 외/불가 → 카드로 폴백.
 * props 는 verbatim 보존(__props) → 라운드트립. 커스텀 타입이므로 HTML/MD 내보내기는 링크로 degrade.
 */

import { neutralizeUrl, resolveEmbed, isAllowedEmbedUrl } from "../../utils/url.js";

function hostOf(url) {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch (_) {
    const m = String(url || "").match(/^(?:https?:\/\/)?([^/?#]+)/i);
    return m ? m[1].replace(/^www\./, "") : "";
  }
}
function ratioPct(ratio) {
  const [w, h] = String(ratio || "16:9").split(":").map((n) => parseFloat(n));
  return w && h ? (h / w) * 100 : 56.25;
}

/** el.__props 를 읽어 카드/임베드 내부 DOM 을 (재)구축. 비동기 메타 갱신 후에도 재사용. */
export function renderBookmarkInner(el) {
  const props = (el && el.__props) || {};
  const wrap = el && el.querySelector(".lumir-bookmark");
  if (!wrap) return;
  wrap.innerHTML = "";
  const url = neutralizeUrl(props.url);
  const mode = props.mode === "embed" ? "embed" : "card";

  // 리사이즈 래퍼 — previewWidth(px) 적용 + 좌/우 핸들(controls.js가 드래그로 previewWidth 갱신).
  const inner = document.createElement("div");
  inner.className = "lumir-bookmark-inner";
  if (props.previewWidth) inner.style.width = parseInt(props.previewWidth, 10) + "px";

  if (mode === "embed") {
    // 저장된 embedUrl 우선, 없으면 원본 url 에서 재도출 — 둘 다 허용 제공자일 때만 iframe.
    let embedUrl = props.embedUrl ? neutralizeUrl(props.embedUrl) : "";
    let ratio = props.ratio || "16:9";
    if (!embedUrl || !isAllowedEmbedUrl(embedUrl)) {
      const emb = resolveEmbed(url);
      if (emb) { embedUrl = emb.embedUrl; ratio = emb.ratio; }
    }
    if (embedUrl && isAllowedEmbedUrl(embedUrl)) {
      wrap.dataset.mode = "embed";
      const frame = document.createElement("div");
      frame.className = "lumir-bookmark-embed";
      frame.style.paddingTop = ratioPct(ratio) + "%";
      const iframe = document.createElement("iframe");
      iframe.src = embedUrl;
      iframe.setAttribute("loading", "lazy");
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
      iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share");
      // sandbox: 스크립트/동일출처(플레이어 동작)·팝업·전체화면 허용, top-navigation 등은 차단.
      iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation");
      frame.appendChild(iframe);
      inner.appendChild(frame);
      addHandles(inner);
      wrap.appendChild(inner);
      return;
    }
    // 임베드 불가 → 아래 카드로 폴백
  }

  wrap.dataset.mode = "card";
  const a = document.createElement("a");
  a.className = "lumir-bookmark-card";
  a.href = url || "#";
  a.target = "_blank";
  a.rel = "noopener noreferrer";

  const text = document.createElement("div");
  text.className = "lumir-bookmark-text";
  const title = document.createElement("div");
  title.className = "lumir-bookmark-title";
  title.textContent = props.title || props.url || "링크";
  text.appendChild(title);
  if (props.description) {
    const desc = document.createElement("div");
    desc.className = "lumir-bookmark-desc";
    desc.textContent = props.description;
    text.appendChild(desc);
  }
  const site = document.createElement("div");
  site.className = "lumir-bookmark-site";
  if (props.favicon) {
    const fav = document.createElement("img");
    fav.className = "lumir-bookmark-fav";
    fav.src = neutralizeUrl(props.favicon);
    fav.alt = "";
    fav.loading = "lazy";
    fav.addEventListener("error", () => fav.remove());
    site.appendChild(fav);
  }
  const host = document.createElement("span");
  host.textContent = props.site || hostOf(props.url);
  site.appendChild(host);
  text.appendChild(site);
  a.appendChild(text);

  if (props.image) {
    const thumb = document.createElement("div");
    thumb.className = "lumir-bookmark-thumb";
    const img = document.createElement("img");
    img.src = neutralizeUrl(props.image);
    img.alt = "";
    img.loading = "lazy";
    img.addEventListener("error", () => thumb.remove());
    thumb.appendChild(img);
    a.appendChild(thumb);
  }
  inner.appendChild(a);
  addHandles(inner);
  wrap.appendChild(inner);
}

/** 좌/우 리사이즈 핸들 삽입(폭 조절). controls.js(attachBookmarkControls)가 드래그 처리. */
function addHandles(inner) {
  inner.insertAdjacentHTML(
    "beforeend",
    '<div class="lumir-bookmark-handle left"></div><div class="lumir-bookmark-handle right"></div>',
  );
}

export function bookmarkBlockToElement(block) {
  if (typeof document === "undefined") throw new Error("bookmarkBlockToElement: DOM 필요");
  const el = document.createElement("div");
  el.className = "block";
  el.dataset.type = "bookmark";
  if (block && block.id != null) el.dataset.id = String(block.id);
  el.__props = block && block.props ? { ...block.props } : {};
  el.__children = block && Array.isArray(block.children) ? block.children : [];
  const wrap = document.createElement("div");
  wrap.className = "lumir-bookmark";
  wrap.setAttribute("contenteditable", "false");
  el.appendChild(wrap);
  renderBookmarkInner(el);
  return el;
}

export function bookmarkElementToBlock(el) {
  const block = {};
  if (el.dataset.id != null) block.id = el.dataset.id;
  block.type = "bookmark";
  block.props = el.__props || {};
  // 파일형 블록과 동일: content 키 없음(no content).
  block.children = el.__children || [];
  return block;
}
